import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { CLASSROOMS_KEY } from '@teetsh/app/src/queries/classrooms/useClassrooms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { TAGS_KEY } from '@teetsh/app/src/queries/tags/useTags';
import { TagWithClassrooms } from '@teetsh/app/src/containers/MesAnneesScolaires/School/Tags/types';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import { useTranslation } from 'react-i18next';

function createTagJoin({
  schoolyearId,
  schoolId,
  classroomId,
  tagId,
}: {
  schoolyearId: string;
  schoolId: string;
  classroomId: string;
  tagId: string;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/classrooms/${classroomId}/tags/${tagId}`;
  return request(requestURL, {
    method: 'POST',
  });
}

interface Props {
  schoolyearId: string;
  schoolId: string;
}

export default function useCreateTagJoin(props: Props) {
  const { schoolyearId, schoolId } = props;
  const { notifyError } = useNotification();
  const { t } = useTranslation(['schoolyearSettings']);
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: ({
      tagId,
      classroomId,
    }: {
      tagId: string;
      classroomId: string;
    }) =>
      createTagJoin({
        schoolId,
        schoolyearId,
        classroomId,
        tagId,
      }),
    onMutate: ({ tagId, classroomId }) => {
      queryClient.setQueryData<TagWithClassrooms[]>(
        [schoolyearId, schoolId, TAGS_KEY],
        data =>
          (data || []).map(g =>
            g.id === tagId
              ? {
                  ...g,
                  classrooms: [...g.classrooms, { id: classroomId }],
                }
              : g,
          ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLASSROOMS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [schoolyearId, schoolId, EDT_KEY],
      });
    },
    onError: () => {
      notifyError({
        title: t('schoolyearSettings:messages.tag.createError'),
      });
      queryClient.invalidateQueries({
        queryKey: [schoolyearId, schoolId, TAGS_KEY],
      });
    },
  });
  return mutateAsync;
}
